import { SupportedLanguage } from 'lib/lexemes/supported-languages';
import { ContentIdList } from 'lib/api';

export function replaceRelativeUrlsWithProperAbsoluteUrls(unescapedHtmlWithUrls: string) {
    return unescapedHtmlWithUrls;
}

export type { ContentIdList } from 'lib/api/get-contents-id-list';

export function replaceVariablesWithProperUrls(
    unescapedHtmlWithUrls: string,
    variable: RegExp,
    newValue: string
) {
    return unescapedHtmlWithUrls?.replace(variable, newValue);
}

export function convertDecimalToHexColor(decimalColor: number) {
    if (decimalColor) {
        return `#${decimalColor.toString(16)}`;
    }

    /**
     * graceful degradation and handling edge-cases
     */
    return 'transparent';
}

// Removes unnecessary spaces from query to make it easier to read in the logs
export function trimQuery(query: string) {
    return query.replace(/(\s|\n)+/g, ' ');
}

export function generateUrlFromText(text: string) {
    return text && encodeURI(text.toLocaleLowerCase());
}

export const prepareDescription = (
    description: string,
    lang: SupportedLanguage,
    listOfUrls?: ContentIdList | null
) => {
    const updatedDescription = replaceVariablesWithProperUrls(
        description,
        /\{\{feedback_url\}\}/g,
        `/${lang}/feedback`
    )
        .replace(/data-image="([0-9])+?"/g, '')
        //
        .replace(/href="\/(.+?)"/g, (item) => {
            const sectionId = item.match(/(\d+)/g);

            if (sectionId && Array.isArray(sectionId) && listOfUrls) {
                const sectionIndex = sectionId[sectionId.length - 1].toString();
                const foundEntry = listOfUrls[sectionIndex];

                if (foundEntry) {
                    if (foundEntry.section < 0) {
                        return `href="/safety-centre/${lang}?section=${foundEntry.section}&entry=${foundEntry.article}"`;
                    } else {
                        return `href="/safety-centre/${lang}/guides?section=${foundEntry.section}&entry=${foundEntry.article}"`;
                    }
                }
            }

            const absoluteUrl = item.replace(/href="\/(.+?)"/g, '$1');

            return `href="https://badoo.com/${lang}/${absoluteUrl}"`;
        })
        // this is a hack to force translation for Badoo MW
        .replace(/(fr|es|en|pt|ru)\/help#/g, '$1/help#$1/');

    return updatedDescription;
};

export function setHtmlNonBreakingSpace(text: string) {
    return text.replace(/\{\{__NB_SPACE__\}\}/g, '&nbsp;');
}

export function removeExtraHtmlBreakLines(text: string) {
    return text.replace(/<p><br><\/p>/g, '');
}

export const cleanupUrl = (url: string) => {
    const nbsp = new RegExp(String.fromCharCode(160), 'g');
    const updatedUrl = url
        .replace(/;/g, '-')
        .replace(/,/g, '-')
        .replace(/\?/g, '')
        .replace(/:/g, '-')
        .replace(/@/g, '-')
        .replace(/=/g, '-')
        .replace(/\+/g, '-')
        .replace(/\$/g, '-')
        .replace(/&/g, '-')
        .replace(/\(/g, '-')
        .replace(/\)/g, '-')
        .replace(/%20/g, '-')
        .replace(/\s/g, '-')
        .replace(nbsp, '-')
        .replace(/’/g, '-')
        .replace(/'/g, '-')
        .replace(/\(/g, '-')
        .replace(/\)/g, '-')
        .replace(/\//g, '-')
        .replace(/—/g, '-')
        .replace(/-{2,}/g, '-');

    return updatedUrl;
};

export const setUrl = (
    lang: string,
    sectionTitle: string,
    guideTitle: string,
    articleTitle?: string
) => {
    let url = `/${lang}/${cleanupUrl(sectionTitle)}/${cleanupUrl(guideTitle)}`;

    if (articleTitle) {
        url += `/${cleanupUrl(articleTitle)}`;
    }

    return generateUrlFromText(url);
};

export function potentialUtf8toEscapedString(url: string) {
    try {
        // If the string is UTF-8, this will work and not throw an error.
        return escape(url);
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);

        return url;
    }
}

export function extractFirstParagraphFromString(text: string, reg: RegExp, index?: number) {
    if (!text) {
        return '';
    }

    const results = text.match(reg);
    const filteredResults = results && results.filter((item) => item !== '<p><br></p>');

    if (filteredResults && index) {
        return filteredResults[index];
    }

    if (results) {
        return results[0];
    }

    return '';
}

export function arrayEquals(firstArray: [] | RegExpMatchArray, arrayNext: [] | RegExpMatchArray) {
    return (
        Array.isArray(firstArray) &&
        Array.isArray(arrayNext) &&
        firstArray.length === arrayNext.length &&
        firstArray.every((val, index) => val === arrayNext[index])
    );
}

export function urlFragmentsEqual(url: string, urlNext: {}) {
    let urlFromServerList = Array.isArray(urlNext)
        ? urlNext
        : Object.values(urlNext).filter((item) => item);

    /**
     * because nginx and proxy_pass do decoding or urls
     * more on this subject: https://trac.nginx.org/nginx/ticket/262
     * on local / devel / Prod nginx it works, but not on mlan directly.
     */
    urlFromServerList = urlFromServerList.map((item) => escape(item));
    urlFromServerList =
        process.env.NODE_ENV === 'production'
            ? urlFromServerList.map((item) => {
                  let urlFromServerItem = undefined;
                  try {
                      urlFromServerItem = escape(decodeURI(item));
                  } catch (error) {
                      urlFromServerItem = item;
                      console.log(`url error =>`, url, error);
                  }
                  return urlFromServerItem;
              })
            : urlFromServerList.map((item) => item);

    let articleUrlFragments = url.match(/[^/]{2,}/g);
    // @ts-ignore
    articleUrlFragments =
        articleUrlFragments &&
        Array.from(articleUrlFragments)
            .filter((item) => item)
            .map((item: string) => escape(decodeURI(item)));

    if (articleUrlFragments && urlFromServerList.length > articleUrlFragments.length) {
        urlFromServerList = urlFromServerList.filter((item) => {
            return articleUrlFragments && articleUrlFragments.includes(item);
        });

        if (!urlFromServerList.length) {
            return false;
        }
    }

    if (!articleUrlFragments) {
        return false;
    }

    // @ts-ignore
    return arrayEquals(articleUrlFragments, urlFromServerList);
}

export const setupProperPartnerId = (lexeme?: string) => {
    // @TODO - find by #ref and swap Badoo to Bumble in footer lexemes
    // please reffer to Badoo.com footer, the rest should be Badoo, not Bumble
    const updatedLexeme = lexeme && lexeme.replace('Bumble', 'Badoo');
    return updatedLexeme as string;
};
